import type { ConversationThread, Message } from 'types/interfaces/message';
import type {
  OfferThreadContext,
  ThreadEvent,
  ThreadEventKind,
  ThreadMessageKind,
  ThreadSubjectType,
  TradeRole,
} from 'types/interfaces/thread';
import { isCaseContext, isOfferContext, isTradeContext } from 'types/interfaces/thread';
import { caseReasonShortLabel } from 'constants/caseReasons';
import { formatCaseNumber } from 'utils/caseNumber';
import { SYSTEM_USERNAME } from 'utils/messageHelpers';

/**
 * Pure helpers for the open thread: its title, the composer placeholder, the
 * lifecycle steppers and the message/event timeline (Asana 1219952262403623,
 * Decisions 4, 11 and 12).
 */

export const threadSubjectType = (thread: Pick<ConversationThread, 'subjectType' | 'otherUser'>): ThreadSubjectType => {
  if (thread.subjectType) return thread.subjectType;
  return thread.otherUser.username === SYSTEM_USERNAME ? 'SYSTEM' : 'DIRECT';
};

/** Mirrors the backend's subjectTitle rules so the header matches the list row. */
export const threadTitle = (thread: ConversationThread): string => {
  const context = thread.context;
  if (isTradeContext(context)) {
    const first = context.items[0]?.name;
    return first ? `Trade ${context.tradeNumber} · ${first}` : `Trade ${context.tradeNumber}`;
  }
  if (isCaseContext(context)) {
    const number = formatCaseNumber(context.caseNumber);
    const reason = context.reason ? caseReasonShortLabel(context.reason) : 'Admin summoned';
    return number ? `Case ${number} · ${reason}` : `Case · ${reason}`;
  }
  if (isOfferContext(context)) {
    return `Trade offer · ${context.give.count} for ${context.get.count} cards`;
  }
  return threadSubjectType(thread) === 'SYSTEM' ? SYSTEM_USERNAME : thread.otherUser.username;
};

export const composerPlaceholder = (thread: ConversationThread): string => {
  const context = thread.context;
  if (isCaseContext(context)) {
    return `Reply to the case. ${thread.otherUser.username} and admins will see this.`;
  }
  if (isTradeContext(context)) return `Reply about Trade ${context.tradeNumber}…`;
  if (isOfferContext(context)) return 'Reply about this trade offer…';
  return `Reply to ${thread.otherUser.username}…`;
};

// Lifecycle steppers (Decision 12). done = green, current = orange ring, todo = grey.

export type StepState = 'done' | 'current' | 'todo' | 'cancelled';

export interface LifecycleStep {
  label: string;
  state: StepState;
}

const stepsFrom = (labels: string[], doneCount: number, cancelledFrom?: number): LifecycleStep[] =>
  labels.map((label, i) => {
    if (cancelledFrom !== undefined && i >= cancelledFrom) return { label, state: 'cancelled' };
    if (i < doneCount) return { label, state: 'done' };
    if (i === doneCount) return { label, state: 'current' };
    return { label, state: 'todo' };
  });

/*
 * Three steps (Eric, 2026-10-28): nothing records a delivery, so completion
 * (the receiver marking the trade complete) is the only arrival signal. The
 * ring sits on the next step not yet done.
 */
const TRADE_STEP_LABELS = ['Initiated', 'Shipped', 'Completed'];

/** Trade statuses as the backend sends them: PENDING_FUNDING, READY, TRANSIT, COMPLETED, CANCELLED. */
export const tradeSteps = (status: string, shippedAt?: string | null): LifecycleStep[] => {
  switch (status.toUpperCase()) {
    case 'COMPLETED':
      return stepsFrom(TRADE_STEP_LABELS, 3);
    case 'CANCELLED':
      // Grey Cancelled in place of Completed; Shipped stays done if it had shipped.
      return [
        { label: 'Initiated', state: 'done' },
        { label: 'Shipped', state: shippedAt ? 'done' : 'todo' },
        { label: 'Cancelled', state: 'cancelled' },
      ];
    case 'TRANSIT':
      return stepsFrom(TRADE_STEP_LABELS, 2);
    default:
      // PENDING_FUNDING, READY, PREPARING and anything unknown: created, awaiting shipping
      return stepsFrom(TRADE_STEP_LABELS, 1);
  }
};

const CASE_STEP_LABELS = ['Case opened', 'Admin reviewing', 'Resolved'];

export const caseSteps = (status: string): LifecycleStep[] => {
  switch (status.toUpperCase()) {
    case 'RESOLVED':
    case 'CLOSED':
      return stepsFrom(CASE_STEP_LABELS, 3);
    case 'IN_PROGRESS':
      return stepsFrom(CASE_STEP_LABELS, 2);
    default:
      // UNREAD, READ: opened, waiting on an admin
      return stepsFrom(CASE_STEP_LABELS, 1);
  }
};

export const offerSteps = (offer: Pick<OfferThreadContext, 'status' | 'createdTradeNumbers'>): LifecycleStep[] => {
  const status = offer.status.toUpperCase();
  const outcome: Record<string, string> = {
    ACCEPTED: 'Accepted',
    DECLINED: 'Declined',
    EXPIRED: 'Expired',
    CANCELLED: 'Withdrawn',
    FAILED: 'Failed',
  };
  if (status === 'ACCEPTED') {
    const created = offer.createdTradeNumbers.length > 0;
    return stepsFrom(['Proposed', 'Accepted', 'Trades created'], created ? 3 : 2);
  }
  if (outcome[status]) {
    return stepsFrom(['Proposed', outcome[status]], 2, 2);
  }
  // PENDING and anything unknown: proposed, awaiting an answer
  return stepsFrom(['Proposed', 'Accepted', 'Trades created'], 1);
};

// Inline events (Decision 4)

const EVENT_LABELS: Record<ThreadEventKind, string> = {
  CREATED: 'Trade created',
  SHIPPED: 'Marked shipped',
  COMPLETED: 'Trade completed',
  CANCELLED: 'Trade cancelled',
  PROPOSED: 'Offer proposed',
  ACCEPTED: 'Offer accepted',
  DECLINED: 'Offer declined',
  EXPIRED: 'Offer expired',
  FAILED: 'Offer failed',
  ADMIN_SUMMONED: 'Admin summoned',
  CASE_RESOLVED: 'Case resolved',
  CASE_REOPENED: 'Case reopened',
  ADMIN_JOINED: 'Admin joined',
};

/** Null for a kind this build does not know; the timeline skips those. */
export const eventLabel = (kind: string): string | null =>
  (EVENT_LABELS as Record<string, string>)[kind] ?? null;

/**
 * Case phase rows that read as inline events rather than bubbles (change 2).
 * ADMIN_SUMMONED is the exception: its row is the summon system card.
 */
const EVENT_MESSAGE_KINDS: ReadonlySet<ThreadMessageKind> = new Set<ThreadMessageKind>(['CASE_RESOLVED', 'CASE_REOPENED', 'ADMIN_JOINED']);

export const isEventMessage = (message: Pick<Message, 'kind'>): boolean =>
  !!message.kind && EVENT_MESSAGE_KINDS.has(message.kind);

export type TimelineItem =
  | { kind: 'message'; at: number; message: Message }
  | { kind: 'event'; at: number; label: string; event: ThreadEvent; detail?: string };

/**
 * Messages and lifecycle events merged by time. Events the backend omits or
 * this build does not label never render; ties put the event first so the
 * message that reacts to it reads below it.
 *
 * Case phase rows (change 2): a CASE_RESOLVED, CASE_REOPENED or ADMIN_JOINED
 * message becomes an event divider carrying the row's text as its detail when
 * that says more than the label; an ADMIN_SUMMONED message stays a message
 * (the summon system card). Either way the events[] entry that points at a
 * message in the thread is dropped so nothing renders twice; an entry whose
 * row the viewer does not hold still renders from events[] alone.
 */
export const mergeTimeline = (messages: Message[], events: ThreadEvent[] | undefined): TimelineItem[] => {
  const messageIds = new Set(messages.map((m) => m.id));
  const items: TimelineItem[] = messages.map((message) => {
    const at = Date.parse(message.createdAt);
    if (message.kind && isEventMessage(message)) {
      const label = eventLabel(message.kind) ?? message.kind;
      const content = message.content.trim();
      const detail = content && content.replace(/\.$/, '').toLowerCase() !== label.toLowerCase() ? content : undefined;
      return { kind: 'event', at, label, detail, event: { kind: message.kind, at: message.createdAt, messageId: message.id } };
    }
    return { kind: 'message', at, message };
  });
  for (const event of events ?? []) {
    if (event.messageId && messageIds.has(event.messageId)) continue;
    const label = eventLabel(event.kind);
    const at = Date.parse(event.at);
    if (!label || Number.isNaN(at)) continue;
    items.push({ kind: 'event', at, label, event });
  }
  return items.sort((a, b) => {
    if (a.at !== b.at) return a.at - b.at;
    if (a.kind === b.kind) return 0;
    return a.kind === 'event' ? -1 : 1;
  });
};

/**
 * The tab on /sending or /receiving that lists a trade in the given status, or
 * null when the status is unknown so the page keeps its own default. READY and
 * PENDING_FUNDING sit on Ready for the sender and Preparing for the receiver.
 */
export const tradePageTab = (role: TradeRole, tradeStatus: string | null | undefined): string | null => {
  switch ((tradeStatus ?? '').toUpperCase()) {
    case 'READY':
    case 'PENDING_FUNDING':
      return role === 'SENDER' ? 'ready' : 'preparing';
    case 'TRANSIT':
      return 'transit';
    case 'COMPLETED':
      return 'completed';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return null;
  }
};

interface TradePageLink {
  role: TradeRole;
  /** The TRADE's status (a case passes its `tradeStatus`, not its own). */
  tradeStatus: string | null | undefined;
  partnerUsername: string;
}

/**
 * Deep link to the viewer's trade page for a trade or case: the trade's tab
 * with the partner's username pre-filled in the search field, so the list
 * loads already filtered to that person's trades.
 */
export const tradePagePath = ({ role, tradeStatus, partnerUsername }: TradePageLink): string => {
  const base = role === 'SENDER' ? '/sending' : '/receiving';
  const tab = tradePageTab(role, tradeStatus);
  const params = [tab ? `tab=${tab}` : null, partnerUsername ? `search=${encodeURIComponent(partnerUsername)}` : null].filter(Boolean);
  return params.length > 0 ? `${base}?${params.join('&')}` : base;
};
